import React, { useState, useCallback, useMemo, useEffect } from "react";
import ProjectCard from "../components/ProjectCard";
import { projectsData } from "../data/portfolioData";
import {
  FaFilter,
  FaCode,
  FaShieldAlt,
  FaPalette,
  FaEye,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(3);
  const [isAnimating, setIsAnimating] = useState(false);

  const filters = useMemo(() => [
    { id: "all", label: "All", icon: <FaFilter className="text-sm" /> },
    { id: "cybersecurity", label: "Security", icon: <FaShieldAlt className="text-sm" /> },
    { id: "webDev", label: "Web Dev", icon: <FaCode className="text-sm" /> },
    { id: "graphicDesign", label: "Design", icon: <FaPalette className="text-sm" /> },
  ], []);

  // Calculate cards per view based on screen size
  useEffect(() => {
    const updateCardsPerView = () => {
      if (window.innerWidth >= 1024) {
        setCardsPerView(3);
      } else if (window.innerWidth >= 768) {
        setCardsPerView(2);
      } else {
        setCardsPerView(1);
      }
    };

    updateCardsPerView();
    window.addEventListener('resize', updateCardsPerView);
    return () => window.removeEventListener('resize', updateCardsPerView);
  }, []);

  const getProjects = useCallback(() => {
    let allProjects = [];
    
    if (activeFilter === "all") {
      allProjects = [
        ...projectsData.cybersecurity.map((p) => ({
          ...p,
          category: "Cybersecurity",
        })),
        ...projectsData.webDev.map((p) => ({
          ...p,
          category: "Web Development",
        })),
        ...projectsData.graphicDesign.map((p) => ({
          ...p,
          category: "Graphic Design",
        })),
      ];
    } else {
      const categoryMap = {
        cybersecurity: projectsData.cybersecurity,
        webDev: projectsData.webDev,
        graphicDesign: projectsData.graphicDesign,
      };
      allProjects = (categoryMap[activeFilter] || []).map((p) => ({
        ...p,
        category: activeFilter === "cybersecurity" ? "Cybersecurity" : activeFilter === "webDev" ? "Web Development" : "Graphic Design",
      }));
    }
    
    return allProjects;
  }, [activeFilter]);

  const projects = useMemo(() => getProjects(), [getProjects]);
  const maxIndex = Math.max(0, projects.length - cardsPerView);

  // Reset index when filter changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [activeFilter, cardsPerView]);

  const nextSlide = useCallback(() => {
    if (!isAnimating && currentIndex + 1 <= maxIndex) {
      setIsAnimating(true);
      setCurrentIndex(prev => prev + 1);
      setTimeout(() => setIsAnimating(false), 300);
    }
  }, [currentIndex, maxIndex, isAnimating]);

  const prevSlide = useCallback(() => {
    if (!isAnimating && currentIndex - 1 >= 0) {
      setIsAnimating(true);
      setCurrentIndex(prev => prev - 1);
      setTimeout(() => setIsAnimating(false), 300);
    }
  }, [currentIndex, isAnimating]);

  const currentProjects = projects.slice(currentIndex, currentIndex + cardsPerView);

  return (
    <section id="projects" 
    className="relative min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950 overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-cyber-grid opacity-5" />
      <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl" />

      {/* Main container - Centered */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 lg:py-12">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">
            Projects & <span className="gradient-text">Portfolio</span>
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Curated selection of my work across web development, cybersecurity, and design
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-300 text-sm
                ${
                  activeFilter === filter.id
                    ? "bg-gradient-to-r from-emerald-500 to-cyan-500 text-white shadow-md"
                    : "bg-white/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 hover:bg-emerald-50 dark:hover:bg-gray-700/80"
                }`}
            >
              {filter.icon}
              <span>{filter.label}</span>
            </button>
          ))}
        </div>

        {/* Carousel Section - One card shifts at a time */}
        {projects.length > 0 ? (
          <div className="relative">
            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              disabled={currentIndex === 0 || isAnimating}
              className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/90 dark:bg-gray-800/90 shadow-lg transition-all duration-300 ${
                currentIndex === 0 || isAnimating
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:shadow-xl hover:bg-emerald-50 dark:hover:bg-gray-700"
              }`}
              aria-label="Previous projects"
            >
              <FaChevronLeft className="text-sm" />
            </button>

            <button
              onClick={nextSlide}
              disabled={currentIndex + cardsPerView >= projects.length || isAnimating}
              className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white/90 dark:bg-gray-800/90 shadow-lg transition-all duration-300 ${
                currentIndex + cardsPerView >= projects.length || isAnimating
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:shadow-xl hover:bg-emerald-50 dark:hover:bg-gray-700"
              }`}
              aria-label="Next projects"
            >
              <FaChevronRight className="text-sm" />
            </button>

            {/* Projects Carousel - Smooth transition */}
            <div className="overflow-hidden">
              <div
                className={`flex justify-center gap-4 md:gap-6 transition-all duration-300 ease-out ${
                  isAnimating ? 'opacity-50' : 'opacity-100'
                }`}
              >
                {currentProjects.map((project, index) => (
                  <div
                    key={`${currentIndex}-${index}`}
                    className="w-full max-w-[320px] md:w-[320px] transition-all duration-300 animate-slideIn"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <ProjectCard project={project} />
                  </div>
                ))}
              </div>
            </div>

            {/* Progress indicator */}
            {projects.length > cardsPerView && (
              <div className="flex justify-center items-center gap-2 mt-6">
                <button
                  onClick={prevSlide}
                  disabled={currentIndex === 0}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-emerald-500 transition-colors"
                >
                  Previous
                </button>
                <div className="flex gap-1.5">
                  {Array.from({ length: projects.length - cardsPerView + 1 }).map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        if (!isAnimating) {
                          setIsAnimating(true);
                          setCurrentIndex(idx);
                          setTimeout(() => setIsAnimating(false), 300);
                        }
                      }}
                      className={`transition-all duration-300 rounded-full ${
                        currentIndex === idx
                          ? "w-4 h-1.5 bg-gradient-to-r from-emerald-500 to-cyan-500"
                          : "w-1.5 h-1.5 bg-gray-400 dark:bg-gray-600 hover:bg-emerald-400"
                      }`}
                      aria-label={`Go to slide ${idx + 1}`}
                    />
                  ))}
                </div>
                <button
                  onClick={nextSlide}
                  disabled={currentIndex + cardsPerView >= projects.length}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-emerald-500 transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 flex items-center justify-center mb-3">
              <FaEye className="text-2xl text-emerald-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
              No projects found
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Try selecting a different category
            </p>
          </div>
        )}

        {/* GitHub Link */}
        <div className="text-center mt-8">
          <a
            href="https://github.com/Wais000"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-white/80 dark:bg-gray-800/80 shadow-md hover:shadow-lg transition-all duration-300 text-sm text-gray-700 dark:text-gray-300 hover:bg-emerald-50 dark:hover:bg-gray-700"
          >
            <FaCode className="text-sm" />
            <span>View More on GitHub</span>
            <FaEye className="text-sm" />
          </a>
        </div>
      </div>

      <style jsx>{`
        /* Gradient text */
        .gradient-text {
          background: linear-gradient(135deg, #10b981, #06b6d4);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        
        /* Slide in animation for cards */
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .animate-slideIn {
          animation: slideIn 0.3s ease-out forwards;
        }
        
        /* Cyber grid background */
        .bg-cyber-grid {
          background-image: 
            linear-gradient(to right, #d1d5db 1px, transparent 1px),
            linear-gradient(to bottom, #d1d5db 1px, transparent 1px);
          background-size: 40px 40px;
        }
        
        .dark .bg-cyber-grid {
          background-image: 
            linear-gradient(to right, #374151 1px, transparent 1px),
            linear-gradient(to bottom, #374151 1px, transparent 1px);
        }
      `}</style>
    </section>
  );
};

export default Projects;